/**
 * AccountService - Service de gestion des comptes utilisateur
 *
 * Ce service gère l'authentification, la validation des licences,
 * et la communication avec le système de comptes.
 *
 * Architecture KISS avec 3 composants principaux :
 * - AccountService (Renderer) : Interface UI et communication IPC
 * - AccountManager (Main Process) : Gestion complète des comptes
 * - WebAdmin (Web Client) : Interface d'administration
 */

// Types de base pour le système de comptes
export interface UserProfile {
  id: string;
  login: string;
  email: string;
  name?: string;
  organization?: string;
  isActive: boolean;
  roles: Role[];
  license?: License; // Seulement si userType === LICENSED
  machineFingerprint?: string; // Cohérence avec le système de licence
  createdAt: Date;
  updatedAt: Date;
}

export interface Role {
  id: string;
  name: string;
  createdAt: Date;
}

export interface LoginCredentials {
  login: string; // Peut être email ou nom d'utilisateur
  password: string;
}

export interface PasswordChange {
  oldPassword: string;
  newPassword: string;
}

export interface CreateUserData {
  login: string;
  email: string;
  password: string;
  name?: string;
  organisationId?: string | null;
  roleIds: string[];
  licenseId?: string | null;
  termsVersionId?: string | null;
  parentUserId?: string | null;
  /** Speaker only: linked license ids (0..N). */
  speakerLicenseIds?: string[];
}

export interface UpdateUserData {
  login?: string;
  email?: string;
  name?: string;
  organisationId?: string | null;
  isActive?: boolean;
  roleIds?: string[];
  licenseId?: string | null;
  termsVersionId?: string | null;
  parentUserId?: string | null;
  /** Speaker only: linked license ids (0..N); omitted = leave unchanged when not Speaker. */
  speakerLicenseIds?: string[];
}

export interface License {
  id: string;
  key: string;
  maxAthletes: number;
  expirationDate: Date;
  isActive: boolean;
  userId: string;
  createdAt: Date;
  updatedAt: Date;
  hasBackupAccess: boolean;
  hasLiveAccess?: boolean;
  hasCustomDisplayAccess?: boolean;
  hasControlRoomAccess?: boolean;
  hasPlanningAccess?: boolean;
  hasLayoutEditorAccess?: boolean;
  hasRemoteCustomDisplayAccess?: boolean;
  maxCustomScreens?: number;
  maxCustomWidgets?: number;
  status?: "CREATED" | "PENDING_TERMS_ACCEPTANCE" | "ACTIVE" | "REVOKED";
  requiresTermsAcceptance?: boolean;
  termsVersionId?: string | null;
  machineName: string;
  daysUntilExpiration: number;
}

// Interfaces API simplifiées
export interface LoginResponse {
  user: UserProfile;
  license?: License;
  expiresAt: string;
}

export interface UsersListResponse {
  users: UserProfile[];
  total: number;
}

export interface UserResponse {
  user: UserProfile;
}

export interface LoginRequest {
  login: string;
  password: string;
}

export interface CreateUserRequest {
  login: string;
  email: string;
  password: string;
  name?: string;
  organization?: string;
  roleIds: string[];
}

export interface UpdateUserRequest {
  login?: string;
  email?: string;
  name?: string;
  organization?: string;
  isActive?: boolean;
  roleIds?: string[];
}

// Types d'erreurs spécifiques aux comptes
export enum AccountError {
  // Erreurs d'authentification
  INVALID_CREDENTIALS = "INVALID_CREDENTIALS",
  ACCOUNT_DISABLED = "ACCOUNT_DISABLED",
  LICENSE_EXPIRED = "LICENSE_EXPIRED",
  LICENSE_TERMS_NOT_ACCEPTED = "LICENSE_TERMS_NOT_ACCEPTED",

  // Erreurs de validation
  EMAIL_INVALID = "EMAIL_INVALID",
  PASSWORD_TOO_WEAK = "PASSWORD_TOO_WEAK",

  // Erreurs système
  NETWORK_ERROR = "NETWORK_ERROR",
  RATE_LIMIT_EXCEEDED = "RATE_LIMIT_EXCEEDED",
  CONNECTION_REQUIRED = "CONNECTION_REQUIRED",
}

// État global du service AccountService
export interface AccountState {
  isConnected: boolean; // Connexion temporaire pour validation licence
  isLoading: boolean;
  user: UserProfile | null;
  license: License | null;
  error: AccountError | null;
  connectionRequired: boolean;
  connectionReason: string | null;
}

// Actions du service AccountService
export interface AccountActions {
  // Authentification
  login: (credentials: LoginCredentials) => Promise<LoginResponse>;
  logout: () => Promise<void>;
  changePassword: (passwords: PasswordChange) => Promise<void>;

  // Gestion des erreurs
  clearError: () => void;

  // Gestion des connexions temporaires
  handleConnectionRequired: (reason: string) => void;
  showConnectionModal: (reason: string) => void;
  validateLicenseAccess: () => Promise<boolean>;

  // Validation temporaire (pas de session maintenue)
  validateWithLicenseSystem: (userId: string) => Promise<boolean>;
}

// Configuration des déclencheurs de connexion
export const CONNECTION_TRIGGERS = {
  FIRST_ACTIVATION: {
    condition: "licenseId === null && userType === LICENSED",
    reason: "Première activation de licence requise",
    priority: "HIGH",
  },
  LICENSE_EXPIRED: {
    condition: "expirationDate < now()",
    reason: "Licence expirée - renouvellement requis",
    priority: "CRITICAL",
  },
  PERIODIC_VALIDATION: {
    interval: 7 * 24 * 60 * 60 * 1000, // 7 jours
    reason: "Validation périodique programmée",
    priority: "LOW",
  },
} as const;

// Règles de validation des mots de passe
export interface PasswordRules {
  minLength: number;
  requireUppercase: boolean;
  requireNumbers: boolean;
}

export const PASSWORD_RULES: PasswordRules = {
  minLength: 8,
  requireUppercase: true,
  requireNumbers: true,
};

// Validation des mots de passe
export const validatePassword = (
  password: string,
): { valid: boolean; errors: string[] } => {
  const errors: string[] = [];

  // Longueur minimale
  if (password.length < PASSWORD_RULES.minLength) {
    errors.push(
      `Le mot de passe doit contenir au moins ${PASSWORD_RULES.minLength} caractères`,
    );
  }

  // Majuscules
  if (PASSWORD_RULES.requireUppercase && !/[A-Z]/.test(password)) {
    errors.push("Le mot de passe doit contenir au moins une majuscule");
  }

  // Chiffres
  if (PASSWORD_RULES.requireNumbers && !/\d/.test(password)) {
    errors.push("Le mot de passe doit contenir au moins un chiffre");
  }

  return {
    valid: errors.length === 0,
    errors,
  };
};

// Validation des emails
export const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export const isValidEmail = (email: string): boolean => EMAIL_REGEX.test(email);

// Rate limiting adapté pour l'app locale
export const RATE_LIMITS = {
  loginAttempts: 3, // Réduit de 5 à 3 tentatives max
  lockoutDuration: 5, // Réduit de 15 à 5 minutes de blocage
} as const;

// Configuration IPC pour les handlers
export const ACCOUNT_IPC_HANDLERS = {
  LOGIN: "account:login",
  LOGOUT: "account:logout",
  CHANGE_PASSWORD: "account:changePassword",
  SYNC_LICENSE: "account:syncLicense",
  TRIGGER_CONNECTION: "account:triggerConnection",
  CONNECTION_REQUIRED: "account:connectionRequired",
  LICENSE_TERMS_NOT_ACCEPTED: "account:licenseTermsNotAccepted",
  VALIDATE_WITH_LICENSE_SYSTEM: "account:validateWithLicenseSystem",
  VALIDATE_LICENSE_ACCESS: "account:validateLicenseAccess",
} as const;

// Configuration API REST simplifiée
export const ACCOUNT_API_ENDPOINTS = {
  SIGN_IN: "/auth/sign-in",
  SIGN_OUT: "/auth/sign-out",
  USERS: "/users",
  USER_BY_ID: (id: string) => `/users/${id}`,
} as const;

// Types pour les caches
export interface ValidationCache {
  userId: string;
  isValid: boolean;
  timestamp: number;
  ttl: number; // Time to live en millisecondes
}

export interface SecurityCache {
  machineFingerprint: string;
  lastValidation: number;
  suspiciousActivity: boolean;
}

export interface LicenseCache {
  licenseKey: string;
  expirationDate: number;
  maxAthletes: number;
  machineFingerprint: string;
}

// Configuration des caches
export const CACHE_CONFIG = {
  VALIDATION_TTL: 5 * 60 * 1000, // 5 minutes
  SECURITY_FILE: ".electron/.renderer-cache.dat",
  LICENSE_FILE: ".electron/.license.dat",
  APP_CACHE_FILE: ".electron/app-cache.dat",
} as const;

// Classe principale AccountService
export class AccountService {
  private state: AccountState = {
    isConnected: false,
    isLoading: false,
    user: null,
    license: null,
    error: null,
    connectionRequired: false,
    connectionReason: null,
  };

  private validationCache: Map<string, ValidationCache> = new Map();

  constructor() {
    // Initialisation du service
    this.initializeService();
  }

  /**
   * Initialise le service AccountService
   */
  private async initializeService(): Promise<void> {
    // Vérification de l'état de connexion existant
    // Chargement des caches locaux
    // Configuration des listeners IPC
  }

  /**
   * Authentifie un utilisateur avec validation de licence
   */
  async login(credentials: LoginCredentials): Promise<LoginResponse> {
    this.setState({ isLoading: true, error: null });

    try {
      // Validation des credentials
      if (!credentials.login || !credentials.password) {
        throw new Error(AccountError.INVALID_CREDENTIALS);
      }

      // Vérification du cache de validation
      const cacheKey = `${credentials.login}_${Date.now()}`;
      const cached = this.validationCache.get(cacheKey);
      if (
        cached &&
        Date.now() - cached.timestamp < CACHE_CONFIG.VALIDATION_TTL
      ) {
        if (cached.isValid) {
          // Retourner les données du cache si valide
          return this.getCachedLoginResponse(cached.userId);
        }
      }

      // Appel IPC vers AccountManager
      const response = await this.callIPC(
        ACCOUNT_IPC_HANDLERS.LOGIN,
        credentials,
      );

      // Mise à jour du cache de validation
      this.validationCache.set(cacheKey, {
        userId: response.user.id,
        isValid: true,
        timestamp: Date.now(),
        ttl: CACHE_CONFIG.VALIDATION_TTL,
      });

      // Mise à jour de l'état
      this.setState({
        isConnected: true,
        isLoading: false,
        user: response.user,
        license: response.license || null,
        error: null,
      });

      return response;
    } catch (error) {
      this.setState({
        isLoading: false,
        error:
          error instanceof Error
            ? (error.message as AccountError)
            : AccountError.NETWORK_ERROR,
      });
      throw error;
    }
  }

  /**
   * Déconnecte l'utilisateur et nettoie les caches
   */
  async logout(): Promise<void> {
    this.setState({ isLoading: true });

    try {
      // Appel IPC vers AccountManager
      await this.callIPC(ACCOUNT_IPC_HANDLERS.LOGOUT, {
        userId: this.state.user?.id,
      });

      // Nettoyage des caches
      this.validationCache.clear();
      this.clearSecurityCache();

      // Mise à jour de l'état
      this.setState({
        isConnected: false,
        isLoading: false,
        user: null,
        license: null,
        error: null,
        connectionRequired: false,
        connectionReason: null,
      });
    } catch (error) {
      this.setState({
        isLoading: false,
        error:
          error instanceof Error
            ? (error.message as AccountError)
            : AccountError.NETWORK_ERROR,
      });
      throw error;
    }
  }

  /**
   * Change le mot de passe de l'utilisateur
   */
  async changePassword(passwords: PasswordChange): Promise<void> {
    this.setState({ isLoading: true, error: null });

    try {
      // Validation des mots de passe
      const validation = validatePassword(passwords.newPassword);
      if (!validation.valid) {
        throw new Error(AccountError.PASSWORD_TOO_WEAK);
      }

      // Appel IPC vers AccountManager
      await this.callIPC(ACCOUNT_IPC_HANDLERS.CHANGE_PASSWORD, {
        userId: this.state.user?.id,
        ...passwords,
      });

      this.setState({ isLoading: false });
    } catch (error) {
      this.setState({
        isLoading: false,
        error:
          error instanceof Error
            ? (error.message as AccountError)
            : AccountError.NETWORK_ERROR,
      });
      throw error;
    }
  }

  /**
   * Valide l'accès à la licence
   */
  async validateLicenseAccess(): Promise<boolean> {
    if (!this.state.user) {
      return false;
    }

    try {
      // Appel IPC vers AccountManager
      const isValid = await this.callIPC(
        ACCOUNT_IPC_HANDLERS.VALIDATE_LICENSE_ACCESS,
        {
          userId: this.state.user.id,
        },
      );

      return isValid;
    } catch (error) {
      this.setState({
        error:
          error instanceof Error
            ? (error.message as AccountError)
            : AccountError.NETWORK_ERROR,
      });
      return false;
    }
  }

  /**
   * Gère les demandes de connexion temporaire
   */
  handleConnectionRequired(reason: string): void {
    this.setState({
      connectionRequired: true,
      connectionReason: reason,
    });
  }

  /**
   * Affiche la modal de connexion
   */
  showConnectionModal(reason: string): void {
    this.handleConnectionRequired(reason);
    // Ici, on déclencherait l'affichage de la modal dans l'UI
  }

  /**
   * Nettoie les erreurs
   */
  clearError(): void {
    this.setState({ error: null });
  }

  /**
   * Valide avec le système de licence
   */
  async validateWithLicenseSystem(userId: string): Promise<boolean> {
    try {
      const isValid = await this.callIPC(
        ACCOUNT_IPC_HANDLERS.VALIDATE_WITH_LICENSE_SYSTEM,
        { userId },
      );
      return isValid;
    } catch (error) {
      return false;
    }
  }

  /**
   * Met à jour l'état du service
   */
  private setState(updates: Partial<AccountState>): void {
    this.state = { ...this.state, ...updates };
    // Ici, on notifierait les composants React du changement d'état
  }

  /**
   * Appel IPC vers le processus principal
   */
  private async callIPC(handler: string, data: any): Promise<any> {
    // Implémentation de l'appel IPC
    // Cette méthode sera implémentée dans l'application Electron
    throw new Error("IPC call not implemented : " + { handler, data });
  }

  /**
   * Récupère la réponse de connexion depuis le cache
   */
  private getCachedLoginResponse(userId: string): LoginResponse {
    // Implémentation de la récupération depuis le cache
    throw new Error("Cache retrieval not implemented : " + { userId });
  }

  /**
   * Nettoie le cache de sécurité
   */
  private clearSecurityCache(): void {
    // Implémentation du nettoyage du cache de sécurité
  }

  /**
   * Obtient l'état actuel du service
   */
  getState(): AccountState {
    return { ...this.state };
  }
}

// Export de l'instance singleton
export const accountService = new AccountService();
